import { type CSSProperties, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavIcon, useBrandTokens, orgLogoHorizontalUrl, orgLogoUrl } from '../shell/shared';
import { PatientsServedDrawer } from './home-dashboard/PatientsServedDrawer';
import { InQueueDrawer } from './home-dashboard/InQueueDrawer';
import { CollectionsDrawer } from './home-dashboard/CollectionsDrawer';
import { OutstandingDrawer } from './home-dashboard/OutstandingDrawer';
import { PatientAlertsDrawer } from './home-dashboard/PatientAlertsDrawer';
import { usePowerSyncQuery, usePowerSyncDb } from '../adapters/powersync';
import { useFormatMoney } from '../hooks/useFormatMoney';
import { useAuth } from '@helios/auth-firebase-web';
import { ensureVisitThenCreateEncounter } from '../services/encounter-service';
import {
  qHomePatientsServedToday,
  qHomeQueueCounts,
  qHomeCollectionsToday,
  qHomeOutstanding,
  qHomeAlerts,
  qHomeQueueSnapshot,
  qHomeScheduleToday,
  qHomeRecentPatients,
  qHomeBillingDetails,
  qPharmacyQueueSummary,
  type HomePatientsServedRow,
  type HomeQueueCountRow,
  type HomeCollectionsRow,
  type HomeOutstandingRow,
  type HomeAlertRow,
  type HomeQueueEntryRow,
  type HomeScheduleRow,
  type HomeRecentPatientRow,
  type HomeBillingDetailRow,
  type PharmacyQueueSummaryRow,
} from '@helios/data-access';

// ─── Helpers ───

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

/** Compute today's day boundaries in user timezone, returned as UTC ISO strings. */
function todayBounds(): { dayStartUtc: string; dayEndUtc: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return { dayStartUtc: start.toISOString(), dayEndUtc: end.toISOString() };
}

function fmtTime(t: string | null): string {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${String(h % 12 || 12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmtRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days === 0)
    return `Today, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function patientStatus(qs: string | null, es: string | null): string {
  if (qs && ['waiting', 'called', 'serving'].includes(qs)) return 'In Queue';
  if (es === 'open') return 'In Consult';
  return 'Completed';
}

// ─── Status chip colors ───

const SC: Record<string, { bg: string; color: string }> = {
  done: { bg: '#dcfce7', color: '#16a34a' },
  serving: { bg: '#dbeafe', color: '#2563eb' },
  waiting: { bg: '#fefce8', color: '#ca8a04' },
  called: { bg: '#fff7ed', color: '#ea580c' },
  next: { bg: '#dc2626', color: '#fff' },
  upcoming: { bg: '#f3f4f6', color: '#6b7280' },
  not_queued: { bg: '#fef2f2', color: '#dc2626' },
  'In Consult': { bg: '#dbeafe', color: '#2563eb' },
  Completed: { bg: '#dcfce7', color: '#16a34a' },
  'In Queue': { bg: '#fefce8', color: '#ca8a04' },
  'Follow-up': { bg: '#f3e8ff', color: '#7c3aed' },
};

const SL: Record<string, string> = {
  done: '✓ Done',
  serving: 'Serving',
  waiting: 'Waiting',
  called: 'Called',
  not_queued: 'Not Queued',
};

// ─── KPI Tile ───

function KpiTile({
  icon,
  value,
  label,
  sub,
  color,
  iconColor,
  onClick,
}: {
  icon: string;
  value: string;
  label: string;
  sub: string;
  color: string;
  iconColor: string;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        flex: '1 1 180px',
        minWidth: 160,
        padding: 'var(--sp-4)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-md)',
          background: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: iconColor,
          marginBottom: 'var(--sp-3)',
        }}
      >
        <NavIcon name={icon} />
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-xl, 24px)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginTop: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: iconColor, marginTop: 2 }}>{sub}</div>
      <div style={{ position: 'absolute', top: 16, right: 16, color: 'var(--text-placeholder)' }}>
        <NavIcon name="chevron-right" />
      </div>
    </div>
  );
}

// ─── Queue Snapshot ───

function QueueSnapshotWidget({ entries }: { entries: HomeQueueEntryRow[] }) {
  const called = entries.filter((e) => e.status === 'called');
  const serving = entries.filter((e) => e.status === 'serving');
  const waiting = entries.filter((e) => e.status === 'waiting');

  const numStyle = (c: string): CSSProperties => ({
    minWidth: 48,
    height: 28,
    padding: '0 8px',
    borderRadius: 'var(--radius-md)',
    background: c,
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  });

  const row = (e: HomeQueueEntryRow, bg: string, border: string, numColor: string) => (
    <div
      key={e.id}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        background: bg,
        border: `1px solid ${border}`,
      }}
    >
      <div style={numStyle(numColor)}>Q-{String(e.queue_number ?? 0).padStart(3, '0')}</div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {e.patient_name ?? 'Unknown'}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {e.service_name ?? '—'}
        </div>
      </div>
    </div>
  );

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-full)',
              background: '#ecfdf5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#059669',
            }}
          >
            <NavIcon name="list-ordered" />
          </div>
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Queue Snapshot
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Live patient queue
            </div>
          </div>
        </div>
        <button
          onClick={() => console.log('Call Next')}
          style={{
            height: 30,
            padding: '0 14px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: '#16a34a',
            color: '#fff',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: 'pointer',
          }}
        >
          Call Next
        </button>
      </div>

      {called.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 8,
            }}
          >
            📣 NOW CALLING
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {called.map((e) => row(e, '#fffbeb', '#fde68a', '#f59e0b'))}
          </div>
        </div>
      )}

      {serving.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 8,
            }}
          >
            ⚡ NOW SERVING
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {serving.map((e) => row(e, '#f0fdf4', '#bbf7d0', '#16a34a'))}
          </div>
        </div>
      )}

      {waiting.length > 0 && (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-semi)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 8,
            }}
          >
            <NavIcon name="users" /> WAITING — {waiting.length} patients
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {waiting.map((e) => row(e, '#f9fafb', '#e5e7eb', '#6b7280'))}
          </div>
        </div>
      )}

      {entries.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            color: 'var(--text-placeholder)',
            fontSize: 'var(--font-size-sm)',
            padding: 'var(--sp-4)',
          }}
        >
          No patients in queue
        </div>
      )}

      <button
        onClick={() => console.log('Open Queue')}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
          padding: 0,
          textAlign: 'center',
        }}
      >
        Open Queue &gt;
      </button>
    </div>
  );
}

// ─── Next Patient ───

function NextPatientWidget({
  queueEntries,
  primary,
  onStartEncounter,
  onOpenPatient,
}: {
  queueEntries: HomeQueueEntryRow[];
  primary: string;
  onStartEncounter?: (patientId: string) => void;
  onOpenPatient?: (patientId: string) => void;
}) {
  // First waiting patient in queue order = next patient
  const next = useMemo(() => {
    return queueEntries.find((e) => e.status === 'waiting') ?? null;
  }, [queueEntries]);

  if (!next) {
    return (
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
          padding: 'var(--sp-4)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: '#431407',
          }}
        >
          My Next Patient
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: '#78350f',
            marginTop: 'var(--sp-3)',
            padding: 'var(--sp-4)',
          }}
        >
          No patients waiting in queue
        </div>
      </div>
    );
  }

  const initials = (next.patient_name ?? '??')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
        padding: 'var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 'var(--radius-full)',
            background: 'rgba(255,255,255,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9a3412',
          }}
        >
          <NavIcon name="user" />
        </div>
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: '#431407',
            }}
          >
            My Next Patient
          </div>
          <div style={{ fontSize: 10, color: '#78350f' }}>Next in queue</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <div
          style={{
            minWidth: 48,
            height: 28,
            padding: '0 8px',
            borderRadius: 'var(--radius-md)',
            background: '#78350f',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          Q-{String(next.queue_number ?? 0).padStart(3, '0')}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: '#78350f' }}>
          Position #{queueEntries.filter((e) => e.status === 'waiting').indexOf(next) + 1} in queue
        </div>
      </div>
      <div
        style={{
          background: 'rgba(255,255,255,0.7)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-full)',
              background: '#fecaca',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: '#991b1b',
            }}
          >
            {initials}
          </div>
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              {next.patient_name ?? 'Unknown'}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text)',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <NavIcon name="clipboard" /> {next.service_name ?? '—'}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
        <button
          onClick={() => next.patient_id && onStartEncounter?.(next.patient_id)}
          style={{
            flex: 1,
            height: 36,
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: primary,
            color: '#fff',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
          }}
        >
          <NavIcon name="activity" /> Start Encounter
        </button>
        <button
          onClick={() => next.patient_id && onOpenPatient?.(next.patient_id)}
          style={{
            height: 36,
            padding: '0 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid rgba(0,0,0,0.15)',
            background: 'rgba(255,255,255,0.6)',
            color: '#1c1917',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          Open Patient <NavIcon name="chevron-right" />
        </button>
      </div>
    </div>
  );
}

// ─── Schedule ───

function ScheduleWidget({
  schedule,
  queueEntries,
  onViewDay,
  onOpenCalendar,
}: {
  schedule: HomeScheduleRow[];
  queueEntries: HomeQueueEntryRow[];
  onViewDay: () => void;
  onOpenCalendar?: () => void;
}) {
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();

  // Build a map of appointment_id → queue status for quick lookup
  // queueEntries don't have appointment_id, so we match by patient_id instead
  const queueByPatient = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of queueEntries) {
      map.set(q.patient_id, q.status);
    }
    return map;
  }, [queueEntries]);

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-full)',
              background: '#dbeafe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2563eb',
            }}
          >
            <NavIcon name="calendar" />
          </div>
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              My Schedule (Today)
            </div>
          </div>
        </div>
        <button
          onClick={onViewDay}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          View Day <NavIcon name="chevron-right" />
        </button>
        {onOpenCalendar && (
          <button
            onClick={onOpenCalendar}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontFamily: 'var(--font-family)',
            }}
          >
            Open Calendar <NavIcon name="chevron-right" />
          </button>
        )}
      </div>
      {schedule.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            color: 'var(--text-placeholder)',
            fontSize: 'var(--font-size-sm)',
            padding: 'var(--sp-4)',
          }}
        >
          No appointments today
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {schedule.map((s, i) => {
          const [h, m] = (s.start_time ?? '0:0').split(':').map(Number);
          const apptMins = h * 60 + m;
          const endMins = apptMins + (s.duration_minutes ?? 30);
          const queueStatus = s.patient_id ? queueByPatient.get(s.patient_id) : undefined;

          let status: string;
          if (queueStatus === 'done') status = 'done';
          else if (queueStatus === 'serving') status = 'serving';
          else if (queueStatus === 'called') status = 'called';
          else if (queueStatus === 'waiting') status = 'waiting';
          else if (nowMins >= endMins) status = 'done';
          else status = 'not_queued';
          const sc = SC[status] ?? { bg: '#f3f4f6', color: '#6b7280' };
          const label = SL[status];
          return (
            <div
              key={s.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: '10px 0',
                borderBottom: i < schedule.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text-muted)',
                  width: 70,
                  flexShrink: 0,
                }}
              >
                {fmtTime(s.start_time)}
              </span>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {s.patient_name ?? 'Unknown'}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {s.service_name ?? '—'}
                </div>
              </div>
              {label && (
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    padding: '2px 10px',
                    borderRadius: 'var(--radius-full)',
                    background: sc.bg,
                    color: sc.color,
                    fontWeight: 'var(--font-weight-semi)',
                  }}
                >
                  {label}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Billing Snapshot ───

function BillingSnapshotWidget({
  outstanding,
  collections,
  details,
}: {
  outstanding: { totalCents: number; count: number };
  collections: { totalCents: number; count: number };
  details: HomeBillingDetailRow | null;
}) {
  const fmtCompact = useFormatMoney({ compact: true });
  // Home dashboard money inputs use a loose "cents-or-major" convention
  // (queries return cents but historically went through a divide-by-100-free
  // formatter). Preserve the original behavior: treat values as already
  // major and round to whole units via the compact formatter.
  const fmt = (amount: number) => fmtCompact(Math.round(amount));
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-full)',
            background: '#fef3c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#d97706',
          }}
        >
          <NavIcon name="credit-card" />
        </div>
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Billing Snapshot
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Financial overview
          </div>
        </div>
      </div>
      <div
        style={{
          padding: 'var(--sp-4)',
          borderRadius: 'var(--radius-md)',
          background: '#fef2f2',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Unpaid Invoices
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-xl, 24px)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            {fmt(outstanding.totalCents)}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: '#dc2626' }}>
            {outstanding.count} invoices
          </div>
        </div>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-full)',
            background: '#fecaca',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#dc2626',
          }}
        >
          <NavIcon name="alert-triangle" />
        </div>
      </div>
      <div
        style={{
          padding: 'var(--sp-4)',
          borderRadius: 'var(--radius-md)',
          background: '#f0fdf4',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Payments Today
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-xl, 24px)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            {fmt(collections.totalCents)}
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: '#16a34a' }}>
            {collections.count} payments
          </div>
        </div>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius-full)',
            background: '#bbf7d0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#16a34a',
          }}
        >
          <NavIcon name="credit-card" />
        </div>
      </div>
      {details && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          {details.oldest_invoice_no && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Oldest unpaid:</span>
              <span
                style={{ color: 'var(--color-primary)', fontWeight: 'var(--font-weight-medium)' }}
              >
                {details.oldest_invoice_no} ({details.oldest_days ?? 0} days)
              </span>
            </div>
          )}
          {details.top_patient_name && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Top unpaid patient:</span>
              <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
                {details.top_patient_name}
              </span>
            </div>
          )}
        </div>
      )}
      <button
        onClick={() => console.log('View Billing')}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
          padding: 0,
          textAlign: 'center',
        }}
      >
        View Billing &gt;
      </button>
    </div>
  );
}

// ─── Recent Patients ───

function RecentPatientsWidget({
  patients,
  onViewAll,
}: {
  patients: HomeRecentPatientRow[];
  onViewAll: () => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-surface)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-full)',
              background: '#f3e8ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#7c3aed',
            }}
          >
            <NavIcon name="users" />
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Recent Patients
          </div>
        </div>
        <button
          onClick={onViewAll}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          View Patients <NavIcon name="chevron-right" />
        </button>
      </div>
      {patients.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            color: 'var(--text-placeholder)',
            fontSize: 'var(--font-size-sm)',
            padding: 'var(--sp-4)',
          }}
        >
          No recent patients
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {patients.map((p, i) => {
          const status = patientStatus(p.queue_status, p.encounter_status);
          const sc = SC[status] ?? { bg: '#f3f4f6', color: '#6b7280' };
          return (
            <div
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: '8px 0',
                borderBottom: i < patients.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {p.full_name ?? 'Unknown'}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {fmtRelative(p.last_visited_at)}
                </div>
              </div>
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-full)',
                  background: sc.bg,
                  color: sc.color,
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                {status}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ───

type KpiDrawer = 'patients-served' | 'in-queue' | 'collections' | 'outstanding' | 'alerts' | null;

export function HomeDashboardPage() {
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const today = useMemo(todayIso, []);
  const { dayStartUtc, dayEndUtc } = useMemo(todayBounds, []);
  const [openDrawer, setOpenDrawer] = useState<KpiDrawer>(null);
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const fmtCompact = useFormatMoney({ compact: true });
  const fmt = (amount: number) => fmtCompact(Math.round(amount));

  const handleStartEncounter = useCallback(
    async (patientId: string, appointmentId: string) => {
      if (!db || !orgId) return;
      try {
        const { encounterId } = await ensureVisitThenCreateEncounter({
          db,
          orgId,
          clinicId: null,
          patientId,
          appointmentId,
        });
        navigate(`/patient/${patientId}/encounters/${encounterId}`);
      } catch (err) {
        console.error('[Home] Start Encounter failed:', err);
      }
    },
    [db, orgId, navigate],
  );

  // ── Queries ──
  const { data: servedRows = [] } = usePowerSyncQuery<HomePatientsServedRow>(
    qHomePatientsServedToday(dayStartUtc, dayEndUtc),
  );
  const { data: queueCountRows = [] } = usePowerSyncQuery<HomeQueueCountRow>(
    qHomeQueueCounts(today),
  );
  const { data: collectionsRows = [] } = usePowerSyncQuery<HomeCollectionsRow>(
    qHomeCollectionsToday(dayStartUtc, dayEndUtc),
  );
  const { data: outstandingRows = [] } = usePowerSyncQuery<HomeOutstandingRow>(qHomeOutstanding());
  const { data: alertRows = [] } = usePowerSyncQuery<HomeAlertRow>(qHomeAlerts());
  const { data: queueEntries = [] } = usePowerSyncQuery<HomeQueueEntryRow>(
    qHomeQueueSnapshot(today),
  );
  const { data: scheduleRows = [] } = usePowerSyncQuery<HomeScheduleRow>(qHomeScheduleToday(today));
  const { data: recentPatients = [] } =
    usePowerSyncQuery<HomeRecentPatientRow>(qHomeRecentPatients());
  const { data: billingDetailRows = [] } =
    usePowerSyncQuery<HomeBillingDetailRow>(qHomeBillingDetails());
  const { data: pharmacySummaryRows = [] } =
    usePowerSyncQuery<PharmacyQueueSummaryRow>(qPharmacyQueueSummary());

  // ── Derived KPIs ──
  const served = servedRows[0]?.count ?? 0;
  const qc = queueCountRows[0];
  const queueTotal = (qc?.waiting ?? 0) + (qc?.called ?? 0) + (qc?.serving ?? 0);
  const coll = collectionsRows[0];
  const out = outstandingRows[0];
  const alerts = alertRows[0];
  const pharm = pharmacySummaryRows[0];
  const pharmacyReady = pharm?.ready_count ?? 0;
  const pharmacyPartial = pharm?.partial_count ?? 0;
  const pharmacyPatients = pharm?.patient_count ?? 0;
  const pharmacySub =
    pharmacyReady === 0
      ? 'No medicines ready'
      : `${pharmacyPartial} partial • ${pharmacyPatients} patient${
          pharmacyPatients === 1 ? '' : 's'
        }`;

  const kpis = [
    {
      icon: 'users',
      value: String(served),
      label: 'Patients Served Today',
      sub: 'Includes walk-ins',
      color: '#f0fdf4',
      iconColor: '#16a34a',
      onClick: () => setOpenDrawer('patients-served'),
    },
    {
      icon: 'list-ordered',
      value: String(queueTotal),
      label: 'In Queue Now',
      sub: `${qc?.waiting ?? 0}W • ${qc?.called ?? 0}C • ${qc?.serving ?? 0}S`,
      color: '#fefce8',
      iconColor: '#ca8a04',
      onClick: () => setOpenDrawer('in-queue'),
    },
    {
      icon: 'credit-card',
      value: fmt(coll?.total_cents ?? 0),
      label: 'Collections Today',
      sub: `${coll?.payment_count ?? 0} payments`,
      color: '#ecfdf5',
      iconColor: '#059669',
      onClick: () => setOpenDrawer('collections'),
    },
    {
      icon: 'file-text',
      value: fmt(out?.total_cents ?? 0),
      label: 'Outstanding',
      sub: `${out?.invoice_count ?? 0} unpaid`,
      color: '#fff7ed',
      iconColor: '#ea580c',
      onClick: () => setOpenDrawer('outstanding'),
    },
    // PRD.200 — Pharmacy widget. Read-only summary; clicking navigates
    // to the global Pharmacy page where dispensing actually happens.
    {
      icon: 'heart',
      value: String(pharmacyReady),
      label: 'Ready to Dispense',
      sub: pharmacySub,
      color: '#eef2ff',
      iconColor: '#4f46e5',
      onClick: () => navigate('/inventory/pharmacy'),
    },
  ];

  // Patient Alerts moved out of the top KPI row but still rendered as a
  // KpiTile so its styling, drawer behavior, and copy are unchanged.
  const patientAlertsKpi = {
    icon: 'alert-triangle',
    value: String(alerts?.total ?? 0),
    label: 'Patient Alerts',
    sub: `${alerts?.critical ?? 0} critical • ${alerts?.warning ?? 0} warning`,
    color: '#fef2f2',
    iconColor: '#dc2626',
    onClick: () => setOpenDrawer('alerts'),
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-5)',
        maxWidth: 1100,
        margin: '0 auto',
        width: '100%',
      }}
    >
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          {(orgLogoHorizontalUrl || orgLogoUrl) && (
            <img
              src={orgLogoHorizontalUrl || orgLogoUrl}
              alt=""
              style={
                orgLogoHorizontalUrl
                  ? {
                      height: 52,
                      maxWidth: 220,
                      objectFit: 'contain',
                      objectPosition: 'left center',
                      display: 'block',
                    }
                  : {
                      width: 48,
                      height: 48,
                      borderRadius: 'var(--radius-full)',
                      objectFit: 'cover',
                      display: 'block',
                    }
              }
            />
          )}
          <p
            style={{
              margin: '6px 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            Today's overview
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <button
            onClick={() => navigate('/patient/new')}
            style={{
              height: 38,
              padding: '0 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <NavIcon name="user-plus" /> Create Patient
          </button>
          <button
            onClick={() => navigate('/clinic/queue')}
            style={{
              height: 38,
              padding: '0 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <NavIcon name="list-ordered" /> Open Queue
          </button>
          <button
            onClick={() => navigate('/appointments/day')}
            style={{
              height: 38,
              padding: '0 16px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <NavIcon name="calendar" /> Book a Patient
          </button>
        </div>
      </div>

      {/* KPI Row */}
      <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        {kpis.map((kpi) => (
          <KpiTile key={kpi.label} {...kpi} />
        ))}
      </div>

      {/* Main 2-Column Layout */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)',
          gap: 'var(--sp-4)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <QueueSnapshotWidget entries={queueEntries} />
          <BillingSnapshotWidget
            outstanding={{ totalCents: out?.total_cents ?? 0, count: out?.invoice_count ?? 0 }}
            collections={{ totalCents: coll?.total_cents ?? 0, count: coll?.payment_count ?? 0 }}
            details={billingDetailRows[0] ?? null}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <NextPatientWidget
            queueEntries={queueEntries}
            primary={primary}
            onStartEncounter={(pid) => handleStartEncounter(pid, '')}
            onOpenPatient={(pid) => navigate(`/patient/${pid}/encounters`)}
          />
          <ScheduleWidget
            schedule={scheduleRows}
            queueEntries={queueEntries}
            onViewDay={() => navigate('/appointments/day')}
            onOpenCalendar={() => navigate('/appointments/month')}
          />
          <RecentPatientsWidget patients={recentPatients} onViewAll={() => navigate('/patients')} />
        </div>
      </div>

      {/* Secondary KPIs — Patient Alerts demoted from the top row but still
          surfaced via the same KpiTile so the drawer wiring carries through. */}
      <div>
        <h2
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            margin: '0 0 var(--sp-3)',
          }}
        >
          Operational Signals
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 'var(--sp-3)',
          }}
        >
          <KpiTile {...patientAlertsKpi} />
        </div>
      </div>

      {/* Clinic Hub */}
      <div>
        <h2
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            margin: '0 0 var(--sp-3)',
          }}
        >
          Clinic Hub
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 'var(--sp-3)',
          }}
        >
          {[
            { label: 'View Patients', icon: 'users', path: '/patients' },
            { label: 'Queue', icon: 'list-ordered', path: '/clinic/queue' },
            { label: 'Appointments', icon: 'calendar', path: '/appointments/month' },
            { label: 'Inventory', icon: 'layout-grid', path: '/inventory/items' },
            { label: 'Pharmacy', icon: 'heart', path: '/inventory/pharmacy' },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-4)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-family)',
                transition: 'box-shadow 150ms, border-color 150ms',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-surface-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                  flexShrink: 0,
                }}
              >
                <NavIcon name={item.icon} />
              </div>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                  lineHeight: 'var(--line-height-sm)',
                }}
              >
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* KPI Drawers */}
      <PatientsServedDrawer
        open={openDrawer === 'patients-served'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        dayStartUtc={dayStartUtc}
        dayEndUtc={dayEndUtc}
        portalStyle={brandTokens as CSSProperties}
      />
      <InQueueDrawer
        open={openDrawer === 'in-queue'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        today={today}
        portalStyle={brandTokens as CSSProperties}
      />
      <CollectionsDrawer
        open={openDrawer === 'collections'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        dayStartUtc={dayStartUtc}
        dayEndUtc={dayEndUtc}
        portalStyle={brandTokens as CSSProperties}
      />
      <OutstandingDrawer
        open={openDrawer === 'outstanding'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        portalStyle={brandTokens as CSSProperties}
      />
      <PatientAlertsDrawer
        open={openDrawer === 'alerts'}
        onOpenChange={(v) => !v && setOpenDrawer(null)}
        alertCounts={{ critical: alerts?.critical ?? 0, warning: alerts?.warning ?? 0 }}
        portalStyle={brandTokens as CSSProperties}
      />
    </div>
  );
}
